/* The intake module's reads, typed once (designs/intake-ui-parity.md UP9).
   Failure shape matches the routes: {error} bodies become thrown Errors the
   screens render by name. */

import type {
  Capability,
  CapabilityBoardRow,
  CapabilityComment,
  CapabilityFiling,
  CapabilityLink,
  CapabilityTracking,
  Stakeholder,
  TrackingPatch,
} from "../../../shared/capabilities";
import type { CanonicalTeam, TeamKind } from "../../../shared/canonical-teams";
import type { Product, Vehicle } from "../../../shared/products";
import type { Project } from "../../../shared/projects";

export interface Me {
  /* `ids`: every id that is this person — the object id, plus the email
     records stamped before one-identity carry (designs/one-identity.md OI9) */
  user: { userId: string; name: string; ids?: string[] } | null;
  roles: string[];
  surface: string;
}

/* "Is this stamp me?" — decides only whether to OFFER an action; the server
   decides whether it is allowed (OI6/OI9). Emails compare without case. */
export function isMine(me: Me | null, stampUserId: string | undefined): boolean {
  if (me?.user == null || stampUserId === undefined) return false;
  const stamp = stampUserId.toLowerCase();
  return (me.user.ids ?? [me.user.userId]).some((id) => id.toLowerCase() === stamp);
}

export interface CapabilityDetail {
  current: Capability;
  origin: Capability;
  stakeholders: Stakeholder[];
  comments: CapabilityComment[];
  links: CapabilityLink[];
  tracking: CapabilityTracking | null;
  score: number;
}

export const asJson = async <T,>(res: Response): Promise<T> => {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
};

/* THE board — one queue, every ask (intake-unified-form.md UF5) */
export const getBoard = async (): Promise<CapabilityBoardRow[]> =>
  asJson(await fetch("/api/intake/capabilities/board"));

/* every effort, current revision — the "duplicate of…" search (UF18/UF19) */
export const getCapabilities = async (): Promise<Capability[]> =>
  asJson(await fetch("/api/intake/capabilities"));

/* the product list (UF21) */
export const getProducts = async (): Promise<Product[]> =>
  asJson(await fetch("/api/intake/products"));

/* the vehicles, every product's (teams-and-products.md TP4) */
export const getVehicles = async (): Promise<Vehicle[]> =>
  asJson(await fetch("/api/intake/vehicles"));

/* a vehicle added from the form — it waits for an Admin's review (TP5) */
export const postVehicle = async (productId: string, name: string): Promise<Vehicle> =>
  asJson(
    await fetch("/api/intake/vehicles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId, name }),
    }),
  );

export const getTeams = async (): Promise<CanonicalTeam[]> =>
  asJson(await fetch("/api/intake/teams"));

export const getMe = async (): Promise<Me> => asJson(await fetch("/api/me"));

/* every effort's filing and the path it came through (intake-input-path.md IP6) */
export const getFilings = async (): Promise<CapabilityFiling[]> =>
  asJson(await fetch("/api/intake/capabilities/filings"));

export const getDetail = async (id: string): Promise<CapabilityDetail> =>
  asJson(await fetch(`/api/intake/capabilities/${id}`));

/* the project picker's list (intake-project-mapping.md IPM6) — the
   existing, ungated /api/projects read; no intake feature owns this route */
export const getProjects = async (): Promise<Project[]> => asJson(await fetch("/api/projects"));

/* the project record's reverse read (IPM5) */
export const getByProject = async (projectId: string): Promise<CapabilityBoardRow[]> =>
  asJson(await fetch(`/api/intake/capabilities/by-project/${projectId}`));

/* the New-project half of the disposition flow (IPM6): draft-only, a name
   is all the form takes here — the project's other fields are edited on
   its own record afterward, exactly like Projects' own New draft. */
export const postProject = async (name: string): Promise<Project> =>
  asJson(
    await fetch("/api/projects", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, roles: [] }),
    }),
  );

export const putTracking = async (id: string, patch: TrackingPatch): Promise<CapabilityTracking> =>
  asJson(
    await fetch(`/api/intake/capabilities/${id}/tracking`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),
  );

export const postTeam = async (input: { name: string; kind?: TeamKind | null }): Promise<CanonicalTeam> =>
  asJson(
    await fetch("/api/intake/teams", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }),
  );

export const putTeam = async (
  id: string,
  edit: { name?: string; kind?: TeamKind | null; retired?: boolean; needsReview?: boolean },
): Promise<CanonicalTeam> =>
  asJson(
    await fetch(`/api/intake/teams/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(edit),
    }),
  );

/* fold a duplicate team into the one that stays (teams-and-products.md TP6) */
export const mergeTeam = async (
  id: string,
  into: string,
): Promise<{ efforts: number; backings: number; products: number }> =>
  asJson(
    await fetch(`/api/intake/teams/${encodeURIComponent(id)}/merge`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ into }),
    }),
  );

export const putVehicle = async (
  id: string,
  edit: { name?: string; retired?: boolean; needsReview?: boolean },
): Promise<Vehicle> =>
  asJson(
    await fetch(`/api/intake/vehicles/${encodeURIComponent(id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(edit),
    }),
  );

/* a team's +1 from the drawer (intake-flow-review.md FR1) — the server's
   validateStakeholderInput stays the authority */
export const postStakeholder = async (id: string, teamId: string, note: string | null): Promise<unknown> =>
  asJson(
    await fetch(`/api/intake/capabilities/${id}/stakeholders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ teamId, note }),
    }),
  );

/* withdrawing a +1 — an Admin correction, tombstoned on the server
   (IDM4; intake-flow-review.md FR5). 204 has no body to parse. */
export const deleteStakeholder = async (id: string, teamId: string): Promise<void> => {
  const res = await fetch(`/api/intake/capabilities/${id}/stakeholders/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
};

export const postComment = async (id: string, body: string): Promise<unknown> =>
  asJson(
    await fetch(`/api/intake/capabilities/${id}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }),
  );
